import React from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {recommendationRebuildCache} from '../../common/constants/queryCacheKey';
import {
  fetchRebuildStatus,
  rebuildState,
  startRebuild,
} from '../../common/services/recommendations';

const formatTime = (utc?: string | null) =>
  utc ? new Date(utc.endsWith('Z') ? utc : `${utc}Z`).toLocaleString() : '';

// Shows the recommendation rebuild that tag and city changes start, and polls
// while one is waiting or running.
export const RebuildStatusBanner: React.FC = () => {
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: [recommendationRebuildCache],
    queryFn: fetchRebuildStatus,
    refetchInterval: query =>
      rebuildState(query.state.data) === 'Idle' ? false : 3000,
  });

  const startMutation = useMutation({
    mutationFn: startRebuild,
    onSuccess: status =>
      queryClient.setQueryData([recommendationRebuildCache], status),
  });

  const status = statusQuery.data;
  const state = rebuildState(status);
  const isBusy = state !== 'Idle';

  let text: string;
  if (startMutation.isError) {
    text = `The rebuild could not be started: ${(startMutation.error as Error).message}`;
  } else if (statusQuery.isPending) {
    text = 'Checking the recommendation vectors…';
  } else if (statusQuery.isError) {
    text = 'Could not read the recommendation rebuild status.';
  } else if (state === 'Running') {
    text = `Rebuilding the recommendation vectors${status?.reason ? ` (${status.reason})` : ''}… This takes a few minutes.`;
  } else if (state === 'Queued') {
    text = `A recommendation rebuild is about to start${status?.reason ? ` (${status.reason})` : ''}.`;
  } else if (status?.lastFinishedAtUtc) {
    text = status.lastSucceeded
      ? `Recommendation vectors are up to date. Last rebuild: ${formatTime(status.lastFinishedAtUtc)}, ${status.lastCitiesProcessed ?? 0} cities.`
      : `The last rebuild failed (${formatTime(status.lastFinishedAtUtc)}): ${status.lastError ?? 'unknown error'}.`;
  } else {
    text =
      'No rebuild since the API started. The vectors are also rebuilt every night at 03:00.';
  }

  const failed =
    startMutation.isError || (!isBusy && status?.lastSucceeded === false);

  return (
    <div
      style={{
        ...bannerStyle,
        borderColor: failed ? '#ff4d4d' : isBusy ? '#daff00' : '#333',
      }}>
      {isBusy && <span style={dotStyle} />}
      <span style={{flex: 1}}>{text}</span>
      <button
        onClick={() => startMutation.mutate()}
        disabled={isBusy || startMutation.isPending}
        style={isBusy ? disabledButtonStyle : buttonStyle}
        title="Recompute the city statistics and every place and event vector">
        Rebuild now
      </button>
    </div>
  );
};

const bannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  marginBottom: '24px',
  borderRadius: '12px',
  border: '1px solid #333',
  backgroundColor: '#242424',
  color: '#ccc',
  fontSize: '0.85rem',
};

const dotStyle: React.CSSProperties = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#daff00',
  flexShrink: 0,
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: '8px',
  fontWeight: 'bold',
  fontSize: '0.8rem',
  cursor: 'pointer',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #333',
  flexShrink: 0,
};

const disabledButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  color: '#555',
  cursor: 'not-allowed',
};
